import { postalCodeFormat } from "@faire/deprecated-retailer-visitor-shared/consts/postalCodeFormat";
import { isCountryUSA } from "@faire/deprecated-retailer-visitor-shared/lib/countryUtils";
import { isValidPublicUrl } from "@faire/deprecated-retailer-visitor-shared/lib/validation";
import {
  PostalCodeField,
  WebsiteUrlField,
} from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/fields/storeFields";
import { useSignUpEventTracker } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import { handleChangeWithoutErrorWhenEmpty } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_utils/handleChangeWithoutErrorWhenEmpty";
import {
  getBusinnessTypeDetails,
  hasMatchedBusiness,
} from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_utils/signupUtilities";
import { useSignUpRequest } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { Step } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/Step";
import { PhysicalStoreSearchInput } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/store-info/__internal__/PhysicalStoreSearchInput";
import { isPostalCodeOptional } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/store-info/__internal__/storeInfoUtil";
import { useLookupGooglePlace } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/store-info/__internal__/useGoogleLookupPlace";
import { trackSignupSignUpFormStep3StoreInfoView } from "@faire/web-api--source/events/signup/view/signUpFormStep3StoreInfo";
import { IMatchedBusiness } from "@faire/web-api--source/indigofair/data/IMatchedBusiness";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Input } from "@faire/web/slate/Input";
import { Spacer } from "@faire/web/slate/spacing";
import { postalCodeRegexPatternForCountry } from "@faire/web/ui/Addresses/addressSchema";
import { Collapsable } from "@faire/web/ui/Collapsable/Collapsable";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Form, useFormikContext } from "formik";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import * as Yup from "yup";

export type StoreInfoData = Pick<
  ISignUpRequest,
  "matched_business" | "website_url" | "legal_business_name" | "postal_code"
> & {
  store_name?: string;
};

export const StoreInfoForm: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const {
    getFieldProps,
    touched,
    errors,
    dirty,
    isValid,
    setFieldValue,
    handleChange,
    values,
    initialValues,
    setFieldTouched,
    handleBlur,
  } = useFormikContext<StoreInfoData>();
  const [{ business_platforms, country, reported_creation_time }] =
    useSignUpRequest([
      "business_platforms",
      "country",
      "reported_creation_time",
    ]);
  const { trackFieldValue } = useSignUpEventTracker(Step.STORE_INFO);

  // we need this ref to avoid triggering the lookup effect when the website_url is changed
  const websiteUrlRef = useRef(values.website_url);
  const {
    lookup,
    lookupResponse,
    isPending: isLookingUp,
  } = useLookupGooglePlace();

  const { hasOnline, hasPhysicalStore, hasBrickAndMortar, hasPopup } =
    getBusinnessTypeDetails(business_platforms);
  const hasPopUpNoBrickAndMortar = hasPopup && !hasBrickAndMortar;

  const hasRequiredInitialValues =
    !!initialValues.store_name && !!initialValues.matched_business?.name;

  /*
   * only show the postal code when its a physical store and they select manual store name or
   * for good friction when they have a popup and no brick and mortar
   */
  const showPostalCode =
    (hasPhysicalStore &&
      values.matched_business?.type === IMatchedBusiness.Type.MANUAL) ||
    hasPopUpNoBrickAndMortar;

  const optionalPostalCode = isPostalCodeOptional(
    reported_creation_time,
    business_platforms,
    values.matched_business
  );

  const storeNameOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = {
      ...values.matched_business,
      name: e.target.value,
    };
    setFieldValue("matched_business", newValue);
    handleChangeWithoutErrorWhenEmpty(
      e,
      "store_name",
      setFieldTouched,
      handleChange
    );
  };

  const handleSelected = (matchedBusiness?: IMatchedBusiness) => {
    if (!matchedBusiness) {
      setFieldValue("postal_code", "");
      setFieldValue("website_url", "");
      return;
    }
    lookup(matchedBusiness, {
      onSuccess: (response) => {
        setFieldValue("matched_business", response.matched_business);
      },
    });
  };

  useEffect(() => {
    websiteUrlRef.current = values.website_url;
  }, [values.website_url]);

  useEffect(() => {
    const currentUrl = websiteUrlRef.current;
    const newUrl = lookupResponse?.matched_business?.website_url;
    // we just want to set the website_url if it's empty
    if (!currentUrl && newUrl && currentUrl !== newUrl) {
      setFieldValue("website_url", newUrl, true);
    }
  }, [setFieldValue, lookupResponse]);

  useEffect(() => {
    trackSignupSignUpFormStep3StoreInfoView();
  }, []);

  return (
    <Form>
      {hasPhysicalStore && !hasPopUpNoBrickAndMortar ? (
        <PhysicalStoreSearchInput
          storeNameOnChange={storeNameOnChange}
          onSelected={handleSelected}
        />
      ) : (
        <Input
          type="text"
          label={strictLocalize({
            id: "v5vckz",
            defaultMessage: "Store name",
            description: { text: "Store name input label" },
          })}
          data-test-id="store-name-input"
          error={touched.store_name && errors.store_name}
          {...getFieldProps("store_name")}
          onChange={storeNameOnChange}
          onBlur={(e) => {
            trackFieldValue("store_name", values.store_name ?? "");
            handleBlur(e);
          }}
        />
      )}
      <Spacer size="4x" />
      <WebsiteUrlField trackChange={trackFieldValue} isOptional={!hasOnline} />
      <Collapsable
        isCollapsed={!showPostalCode}
        data-test-id="collapsable-section"
      >
        <Spacer size="4x" />
        <PostalCodeField
          isOptional={optionalPostalCode}
          trackChange={trackFieldValue}
          isUSA={isCountryUSA(country)}
        />
      </Collapsable>
      <Spacer size="5x" />
      <FullWidthButton
        data-test-id="next"
        disabled={
          !isValid || (!dirty && !hasRequiredInitialValues) || isLookingUp
        }
        type="submit"
      >
        <StrictLocalMsg
          id="m853T8"
          defaultMessage="Next"
          description={{ text: "Button to visit the next page" }}
        />
      </FullWidthButton>
    </Form>
  );
};

export const useSchema = (options: {
  hasOnline?: boolean;
  country?: string;
  isPostalCodeOptional: boolean;
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { country, hasOnline, isPostalCodeOptional } = options;

  return Yup.object().shape({
    store_name: Yup.string().required(
      strictLocalize({
        id: "ME8dZJ",
        defaultMessage: "Please enter your store name",
      })
    ),
    website_url: (hasOnline
      ? Yup.string().required(
          strictLocalize({
            id: "lEhuQB",
            defaultMessage: "Please enter your website",
          })
        )
      : Yup.string().notRequired()
    )
      .test(
        "valid-url",
        strictLocalize({
          id: "ajU+HT",
          defaultMessage: "Please enter a valid URL",
        }),
        (url) => (url ? isValidPublicUrl(url ?? "") : true)
      )
      .max(
        255,
        strictLocalize({
          id: "fBn8cW",
          defaultMessage: "Cannot be longer than 255 characters",
        })
      ),
    legal_business_name: Yup.string().max(
      255,
      strictLocalize({
        id: "fBn8cW",
        defaultMessage: "Cannot be longer than 255 characters",
      })
    ),
    postal_code: (isPostalCodeOptional
      ? Yup.string().notRequired()
      : Yup.string().test(
          "might-be-required",
          isCountryUSA(country)
            ? strictLocalize({
                id: "lU+pcX",
                defaultMessage: "Please enter your zip code",
              })
            : strictLocalize({
                id: "gI54Fe",
                defaultMessage: "Please enter your postal code",
              }),
          (value, context) => {
            return (
              hasMatchedBusiness(context.parent.matched_business) ||
              !!value?.length
            );
          }
        )
    ).test(
      "invalid-postal-code",
      isCountryUSA(country)
        ? strictLocalize({
            id: "5yKWCH",
            defaultMessage: "Please enter a valid zip code",
            description: {
              text: "Error message that shows if the user enters an invalid zip code",
              img: "https://cdn.faire.com/fastly/e828b872ac34c2d3487890e2b076066825472bfaf754f443a58fce0a65e54ea9.png",
            },
          })
        : strictLocalize({
            id: "k2DBBM",
            defaultMessage: "Please enter a valid postal code",
            description: {
              text: "Error message that shows if the user enters an invalid postal code for their business for the DAC7 reporting form",
            },
          }),
      (postalCode) => {
        return country
          ? postalCode?.match(
              postalCodeRegexPatternForCountry(country, strictLocalize) ??
                postalCodeFormat
            ) !== null
          : (postalCode?.length ?? 0) <= POSTAL_CODE_MAX_LENGTH;
      }
    ),
  });
};

const POSTAL_CODE_MAX_LENGTH = 10;

const FullWidthButton = styled(Button)`
  width: 100%;
`;
